/**
 * Shared facet-ordering helper.
 *
 * Each *Search.js page renders its facet sidebar from a hardcoded array of facet
 * names. There is no shared facet-sidebar component, so this small utility gives
 * the pages a single, consistent ordering convention: boolean (yes/no) facets are
 * pinned to the TOP of the filter list, ahead of the multi-value facets, while the
 * relative order of everything else is preserved.
 *
 * The helper operates purely on the facet-NAME array (not on the facet value
 * structures), so it is agnostic to whether a page renders facets from a
 * `{ value, count }` array or an Elasticsearch `{ buckets: [{ key, doc_count }] }`
 * shape. It detects boolean facets two ways:
 *   1. A curated set of known boolean facet names / name patterns used across the app.
 *   2. (When facet data is passed) a value-shape check: the facet has at most two
 *      distinct values and they are all boolean-like (true/false, 'true'/'false',
 *      yes/no, 0/1).
 */

// Known boolean facet names used in the per-page facet-order arrays.
const KNOWN_BOOLEAN_FACETS = new Set([
  'answered',        // House QIW - "Answered" (Yes/No)
  'sitting_days',    // Calendar - "Sitting Day" (Yes/No)
  'response_status', // Petitions - "Response Status" (is_responded, Yes/No)
  'passed_status',   // Divisions - "Result" (passed Yes/No)
]);

const BOOLEAN_LIKE_VALUES = new Set([
  'true', 'false', 'yes', 'no', '0', '1',
]);

function isBooleanLikeValue(v) {
  if (typeof v === 'boolean') return true;
  if (typeof v === 'number') return v === 0 || v === 1;
  if (typeof v === 'string') return BOOLEAN_LIKE_VALUES.has(v.toLowerCase());
  return false;
}

// Pull the distinct values out of whatever facet shape a page uses.
function distinctFacetValues(facetData) {
  if (!facetData) return null;
  let items = null;
  if (Array.isArray(facetData)) items = facetData;
  else if (Array.isArray(facetData.buckets)) items = facetData.buckets;
  if (!items) return null;
  const vals = items.map((it) => {
    if (it == null) return it;
    if (typeof it === 'object') return 'value' in it ? it.value : it.key;
    return it;
  });
  return [...new Set(vals.map((v) => (typeof v === 'string' ? v : v)))];
}

/**
 * Decide whether a single facet is boolean (yes/no).
 * @param {string} name - facet name
 * @param {object} [facets] - the page's facets object (optional, enables value-shape detection)
 */
export function isBooleanFacet(name, facets) {
  if (KNOWN_BOOLEAN_FACETS.has(name)) return true;
  if (/^(is_|has_)/.test(name)) return true;
  if (facets) {
    const vals = distinctFacetValues(facets[name]);
    if (vals && vals.length > 0 && vals.length <= 2 && vals.every(isBooleanLikeValue)) {
      return true;
    }
  }
  return false;
}

/**
 * Return the facet-name order with boolean (yes/no) facets pinned to the top,
 * preserving the original relative order within each group (stable).
 *
 * @param {string[]} order - the page's facet-name order array
 * @param {object} [facets] - the page's facets object (optional, enables value-shape detection)
 * @returns {string[]} reordered facet-name array
 */
export function sortFacetsBooleansFirst(order, facets) {
  if (!Array.isArray(order)) return order;
  const booleans = [];
  const rest = [];
  order.forEach((name) => {
    if (isBooleanFacet(name, facets)) booleans.push(name);
    else rest.push(name);
  });
  return [...booleans, ...rest];
}
